import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchCategories as fetchCategoriesAPI } from 'api/CategoriesAPI';
import { memoize } from 'proxy-memoize';
import { ICategory, RootState } from 'types';

export interface CategoriesState {
  categories: Record<string, ICategory>;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: CategoriesState = {
  categories: {},
  status: 'idle',
  error: null,
};

export const fetchCategories = createAsyncThunk('categories/fetchCategories', async () => {
  const response = await fetchCategoriesAPI();
  return response;
});

export const categoriesSlice = createSlice({
  name: 'categories',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCategories.fulfilled, (state, action: PayloadAction<ICategory[]>) => {
        state.status = 'succeeded';
        state.categories = action.payload.reduce(
          (acc, cat) => {
            acc[cat.category] = cat;
            return acc;
          },
          {} as Record<string, ICategory>,
        );
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch categories';
      });
  },
});

const EMPTY_CATEGORY = {};

export const getAllCategories = (state: RootState) => state.categories?.categories;
export const selectAllCategories = memoize((state: RootState) =>
  Object.values(state.categories?.categories ?? EMPTY_CATEGORY),
);
export const selectCategoriesStatus = (state: RootState) => state.categories.status;
export const selectCategoryByAbbr = (state: RootState, abbr: string) =>
  state.categories?.categories?.[abbr];

export default categoriesSlice.reducer;
